import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePageBootstrap } from "../../hooks/useBootstrapList";
import { useBootstrapWS } from "../../hooks/useBootstrapWS";
import { PageHeader, KpiTile, SectionCard, StatePill, Pill } from "../../components/Ui";
import { ArrowRight } from "lucide-react";
import { labPageUrl } from "../../lib/workflowLinks";
import MobilePillTabBar from "../../components/mobile/MobilePillTabBar";
import { formatListDate } from "../../lib/purchaseListMeta";

function QueueLink({ to, children, className = "" }) {
  return (
    <Link
      to={to}
      className={`flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-3 hover:bg-accent/60 transition-colors ${className}`}
    >
      {children}
    </Link>
  );
}

function verdictTone(verdict) {
  const v = String(verdict || "").toUpperCase();
  if (v.includes("REJECT") || v === "FAIL") return "danger";
  if (v.includes("REVIEW") || v === "PENDING") return "warning";
  if (["AUTO_PASS", "PASS", "RECORDED", "APPROVED"].includes(v)) return "success";
  return "default";
}

function verdictLabel(verdict) {
  if (!verdict) return "Recorded";
  return String(verdict).replace(/_/g, " ");
}

export default function LabDashboard() {
  const [queueFilter, setQueueFilter] = useState("all");
  const { data, reload } = usePageBootstrap({
    path: "/dashboard/bootstrap",
    wsTypes: ["purchase", "lab", "stack"],
  });
  useBootstrapWS(["purchase", "lab", "stack"], reload);

  const truckPending = data?.lab_pending || [];
  const compositeQueue = data?.composite_queue || [];
  const stackLines = data?.stack_pending_lines || [];
  const recentActivity = data?.recent_lab_activity || [];
  const correctionQueue = data?.correction_queue || [];
  const correctionCount = data?.correction_count ?? correctionQueue.length;

  const filterTabs = useMemo(
    () => [
      {
        id: "all",
        label: "All queues",
        badge: truckPending.length + compositeQueue.length + stackLines.length + correctionCount,
      },
      { id: "truck", label: "Truck", badge: truckPending.length },
      { id: "composite", label: "Composite", badge: compositeQueue.length },
      { id: "stack", label: "Stack", badge: stackLines.length },
      { id: "correction", label: "Needs fix", badge: correctionCount },
      { id: "plant", label: "In plant", badge: 0 },
    ],
    [truckPending.length, compositeQueue.length, stackLines.length, correctionCount],
  );

  const sectionClass = (id) =>
    queueFilter !== "all" && queueFilter !== id ? "hidden lg:block" : undefined;

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="lab-dashboard">
      <PageHeader
        eyebrow="Quality Lab"
        title="Lab Desk"
        description="Pending reports across truck arrival, composite, stack sample, and in-plant analysis."
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
        <KpiTile label="Truck arrival" value={truckPending.length} sub="Awaiting report" accent testId="lab-kpi-truck" />
        <KpiTile label="Composite" value={data?.composite_pending_count ?? 0} sub="Post-unload queue" testId="lab-kpi-composite" />
        <KpiTile label="Needs correction" value={correctionCount} sub="Out-of-range reports" testId="lab-kpi-correction" />
        <KpiTile label="Re-lab" value={data?.relab_count ?? 0} sub="Re-check required" testId="lab-kpi-relab" />
      </div>

      <MobilePillTabBar
        tabs={filterTabs}
        value={queueFilter}
        onChange={setQueueFilter}
        testId="lab-dashboard-queue-filter"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Truck arrival — pending" testId="lab-truck-queue" className={sectionClass("truck")}>
          {truckPending.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No truck samples pending.</p>
          ) : (
            <div className="space-y-2">
              {truckPending.slice(0, 6).map((p) => (
                <QueueLink key={p.id} to={labPageUrl("truck")}>
                  <div className="min-w-0">
                    <div className="font-mono text-sm">{p.code}</div>
                    <div className="text-sm truncate">{p.party_name} · {p.variety}</div>
                  </div>
                  <StatePill state={p.state} />
                </QueueLink>
              ))}
            </div>
          )}
          <Link to={labPageUrl("truck")} className="inline-flex items-center gap-1 text-xs link-brand mt-3">
            Open truck arrival <ArrowRight className="h-3 w-3" />
          </Link>
        </SectionCard>

        <SectionCard title="Composite — pending" testId="lab-composite-queue" className={sectionClass("composite")}>
          {compositeQueue.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No composite samples pending.</p>
          ) : (
            <div className="space-y-2">
              {compositeQueue.slice(0, 6).map((p) => (
                <QueueLink key={p.id} to={labPageUrl("composite")}>
                  <div className="min-w-0">
                    <div className="font-mono text-sm">{p.code}</div>
                    <div className="text-sm truncate">{p.party_name}</div>
                  </div>
                  <StatePill state={p.state} />
                </QueueLink>
              ))}
            </div>
          )}
          <Link to={labPageUrl("composite")} className="inline-flex items-center gap-1 text-xs link-brand mt-3">
            Open composite reports <ArrowRight className="h-3 w-3" />
          </Link>
        </SectionCard>

        <SectionCard title="Needs correction" testId="lab-correction-queue" className={sectionClass("correction")}>
          {correctionCount === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No flagged reports awaiting correction.</p>
          ) : (
            <div className="space-y-2">
              {correctionQueue.slice(0, 6).map((p) => (
                <QueueLink key={`fix-${p.id}`} to={labPageUrl("truck")}>
                  <div className="min-w-0">
                    <div className="font-mono text-sm">{p.code}</div>
                    <div className="text-sm truncate">{p.party_name}</div>
                  </div>
                  <Pill tone="warning">Needs fix</Pill>
                </QueueLink>
              ))}
            </div>
          )}
          <Link to={labPageUrl("truck")} className="inline-flex items-center gap-1 text-xs link-brand mt-3">
            Open truck lab to correct <ArrowRight className="h-3 w-3" />
          </Link>
        </SectionCard>

        <SectionCard title="Stack sample — pending" testId="lab-stack-queue" className={sectionClass("stack")}>
          {stackLines.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No stack sample lines pending.</p>
          ) : (
            <div className="space-y-2">
              {stackLines.slice(0, 6).map((line) => (
                <QueueLink key={line.id || line.sample_code} to={labPageUrl("stacks")}>
                  <div className="min-w-0">
                    <div className="font-mono text-sm">{line.sample_code || "—"}</div>
                    <div className="text-sm truncate">{line.party_name || line.quality || "Stack sample"}</div>
                  </div>
                  <Pill tone="warning">{line.lab_status || "PENDING"}</Pill>
                </QueueLink>
              ))}
            </div>
          )}
          <Link to={labPageUrl("stacks")} className="inline-flex items-center gap-1 text-xs link-brand mt-3">
            Open stack sample reports <ArrowRight className="h-3 w-3" />
          </Link>
        </SectionCard>

        <SectionCard title="Recent lab reports" testId="lab-recent">
          {recentActivity.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No lab reports recorded recently.</p>
          ) : (
            <div className="space-y-2">
              {recentActivity.map((item) => (
                <QueueLink key={item.id} to={labPageUrl(item.tab)}>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {item.label}
                    </div>
                    <div className="text-sm truncate">{item.party_name}</div>
                    <div className="text-xs font-mono text-muted-foreground truncate">{item.reference}</div>
                    {item.created_at && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {formatListDate(item.created_at)}
                      </div>
                    )}
                  </div>
                  <Pill tone={verdictTone(item.verdict)}>{verdictLabel(item.verdict)}</Pill>
                </QueueLink>
              ))}
            </div>
          )}
          <Link to={labPageUrl("plant")} className="inline-flex items-center gap-1 text-xs link-brand mt-3">
            In-plant reports <ArrowRight className="h-3 w-3" />
          </Link>
        </SectionCard>
      </div>
    </div>
  );
}
